import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Paperclip, Phone, Send } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStickToBottomContext } from "use-stick-to-bottom";
import { z } from "zod";
import { AttachmentCard } from "@/components/attachment-card";
import {
  ChatContainerContent,
  ChatContainerRoot,
} from "@/components/chat-container";
import { FileUpload, FileUploadTrigger } from "@/components/file-upload";
import { InteractiveMessage } from "@/components/interactive-message";
import { InviteGate } from "@/components/invite-gate";
import { Loader } from "@/components/loader";
import { Message, MessageContent } from "@/components/message";
import {
  PromptInput,
  PromptInputAction,
  PromptInputActions,
  PromptInputTextarea,
} from "@/components/prompt-input";
import { ScrollButton } from "@/components/scroll-button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useBotChallenge } from "@/hooks/use-bot-challenge";
import { useChat } from "@/hooks/use-chat";
import { useEmbedBridge } from "@/hooks/use-embed-bridge";
import { useEndpointStatus } from "@/hooks/use-endpoint-status";
import { useInviteGate } from "@/hooks/use-invite-gate";
import { getTransportConfig } from "@/lib/runtime-env";
import { cn } from "@/lib/utils";
import type { ChatMessage, TransportMode } from "@/types";

const CONNECT_ACCEPT =
  ".csv,.doc,.docx,.jpg,.jpeg,.pdf,.png,.ppt,.pptx,.txt,.wav,.xls,.xlsx";

const chatSearchSchema = z.object({
  mode: z
    .enum(["reachout-ai", "classic-chat"])
    .catch("reachout-ai")
    .default("reachout-ai"),
  // Forwarded from the home redirect (originally on the partner-page iframe
  // URL). Semicolon-delimited Endpoint Features__c — used to activate
  // BotChallenge without a runtime probe.
  features: z.string().optional(),
  // `d` and `embed` are read by useEmbedBridge from window.location.search;
  // listing them in the schema keeps TanStack Router from stripping them on
  // navigation back to /chat from elsewhere in the app.
  d: z.string().optional(),
  embed: z.enum(["floating", "inline"]).optional(),
});

export const Route = createFileRoute("/chat")({
  validateSearch: chatSearchSchema,
  component: ChatPage,
});

/**
 * Interactive cards (e.g. homework-help subject picker) auto_send user
 * messages prefixed with routing markers like "[SUBJECT:math] Math" or
 * "[STUDY_METHOD:practice] …". The backend uses these markers to disambiguate
 * intent, but displaying them in the user's own bubble looks like junk. Strip
 * leading ALL_CAPS markers before rendering. Non-leading or unrecognized
 * brackets (e.g. "[1]" footnote refs) are left alone.
 */
const USER_MARKER_PREFIX = /^\[[A-Z][A-Z0-9_]*:[^\]]*\]\s*/;
function displayUserContent(text: string): string {
  return text.replace(USER_MARKER_PREFIX, "");
}

function ChatPage() {
  const { mode, features: urlFeaturesRaw } = Route.useSearch();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const { sendMessageSent, sendSessionEnded, districtUuid } = useEmbedBridge();

  // URL features are the partner-page generator's authoritative copy of
  // Endpoint.Features__c at generation time — same source the auto-route
  // used. Treated as canonical for feature-gated UX so we don't need a
  // runtime probe for BotChallenge to activate.
  const urlFeatures = useMemo<string[]>(
    () =>
      (urlFeaturesRaw ?? "")
        .split(";")
        .map((f) => f.trim())
        .filter(Boolean),
    [urlFeaturesRaw],
  );

  const baseConfig = useMemo(() => getTransportConfig(), []);

  // Kill-switch probe. Runs once per session per district. Static maintenance
  // page baked at generation time is the primary defense \u2014 this catches the
  // case where a school's CDN serves a stale live page after a district is
  // disabled.
  const districtId = districtUuid ?? baseConfig.districtId ?? null;
  const endpointStatus = useEndpointStatus(districtId || null);
  const isDistrictDisabled = endpointStatus.state === "disabled";

  // Bot challenge gate. Only districts with the `BotChallenge` Feature
  // require a CAPTCHA before chat starts. The hook is a no-op for everyone
  // else. Kept entirely client-side here; backend enforcement of the WAF
  // token is a follow-up.
  //
  // Source of truth: prefer URL features (the partner page just baked them
  // in) over endpointStatus.features (which depends on a runtime probe to
  // chat-handler over an HTTP entry point that isn't always reachable).
  // Either match activates the gate.
  const botChallengeEnabled =
    urlFeatures.includes("BotChallenge") ||
    (endpointStatus.state === "enabled" &&
      endpointStatus.features.includes("BotChallenge"));
  const botChallenge = useBotChallenge({
    enabled: botChallengeEnabled,
    wafIntegrationUrl: baseConfig.wafIntegrationUrl,
    wafCaptchaApiKey: baseConfig.wafCaptchaApiKey,
  });
  const isBotChallengeBlocking =
    botChallenge.state === "pending" || botChallenge.state === "failed";

  // Invite gate. Three signals can activate it:
  //   1. URL `features` contains a synthetic `InviteRequired` flag baked at
  //      partner-page generation time (mirrors how BotChallenge is plumbed).
  //   2. The `endpoint-status` probe returns `inviteRequired: true` —
  //      direct-visit fallback when the partner page is bypassed.
  //   3. The backend publishes an authFailure of `invalid_invite_code` in
  //      response to a join-session — latches the gate on regardless of
  //      upstream signals. Makes the frontend resilient to partial
  //      deployments and matches the user expectation that an "Invalid
  //      invite code" error always re-prompts for the code.
  // Server-side validation in chat-handler is authoritative; these are
  // signals to the UI about *whether* to render the gate.
  const [authFailureLatched, setAuthFailureLatched] = useState(false);
  const inviteGateEnabled =
    urlFeatures.includes("InviteRequired") ||
    (endpointStatus.state === "enabled" && endpointStatus.inviteRequired) ||
    authFailureLatched;

  // The merged transport config carries the gate-supplied invite code through
  // to `useChat`. `useChat` reads `configRef.current` only at connect time, so
  // any update before the first send (lazy connect) or before the gate calls
  // `connectWithMode` (eager verify) is picked up.
  const [gateInviteCode, setGateInviteCode] = useState<string | undefined>(
    undefined,
  );
  // Merge the URL-derived districtUuid into the transport config so
  // `AppSyncTransport.publishJoinSession` sends `districtId` on every
  // join. Without this, `useChat` only sees `baseConfig.districtId` from
  // the runtime-env (`DISTRICT_ID`), which is unset for the Universal
  // embed — so the chat-handler creates anonymous sessions even though
  // the partner page passed `?d=<Code__c>` on the iframe URL.
  //
  // The same `?d=<Code__c>` is also the district's contact code for
  // Amazon Connect (classic-chat). The reachout-contact-center page
  // generator passes `endpoint_record["Code__c"]` as `?d=`, and the legacy
  // AC widget snippet used that same value as `code:` in
  // `AmazonCustomChatWidget.ChatInterface.init`. So when the URL provides
  // it, override `connectCode` too — the runtime-config value (CONNECT_CODE
  // / NEXT_PUBLIC_CONNECT_CODE) is only a fallback for direct visits where
  // no district is known.
  const config = useMemo(() => {
    const merged = gateInviteCode
      ? { ...baseConfig, inviteCode: gateInviteCode }
      : { ...baseConfig };
    if (districtUuid) {
      merged.districtId = districtUuid;
      merged.connectCode = districtUuid;
    }
    return merged;
  }, [baseConfig, gateInviteCode, districtUuid]);

  // Counselor handover availability. Must derive from the merged `config`
  // (NOT `baseConfig`) so that a Universal-page embed — where runtime-config
  // carries no CONNECT_CODE because it's a per-district value supplied via
  // `?d=<Code__c>` on the iframe URL — still shows the "Talk to a Counselor"
  // button in AI mode. Reading `baseConfig.connectCode` here would mean the
  // button is hidden whenever the connect code only exists in URL state,
  // which is exactly the Universal AI flow.
  //
  // Invariant: if a session can succeed in classic-chat (the same URL +
  // env produce a non-empty `config.connectCode` and `config.connectApiEndpoint`),
  // the AI-mode handover button must be visible. Both this gate and the
  // actual handover request go through `config`, so they can't disagree.
  const canSwitchToCounselor = !!(
    config.connectApiEndpoint && config.connectCode
  );

  const {
    messages,
    status,
    isRemoteTyping,
    remoteTypingName,
    streamingContent,
    error,
    activeMode,
    connectWithMode,
    setMode,
    addLocalMessage,
    sendMessage,
    sendTyping,
    requestHumanAssistance,
    sendAttachment,
    getAttachmentUrl,
    disconnect,
    handoverStatus,
  } = useChat({ config });

  // Latest auth-failure code from the system message stream. The backend
  // publishes these via `mapJoinSessionAuthFailure` when join-session is
  // rejected (e.g. invalid invite code, district disabled).
  const lastAuthFailureCode = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.authFailure && m.authFailureCode) return m.authFailureCode;
    }
    return null;
  }, [messages]);

  const invite = useInviteGate({
    enabled: inviteGateEnabled,
    apiEndpoint: baseConfig.apiEndpoint,
    districtId: districtId ?? undefined,
    authFailureCode: lastAuthFailureCode,
    isConnected: status === "connected",
    botChallengeReady: !isBotChallengeBlocking,
    requestVerify: () => {
      void connectWithMode("reachout-ai");
    },
  });

  // Mirror gate state into top-level state so the next render's config memo
  // includes the code before useChat's configRef snapshot is taken.
  useEffect(() => {
    setGateInviteCode(invite.inviteCode);
  }, [invite.inviteCode]);

  // Latch the gate on when the backend rejects with invalid_invite_code, even
  // if no upstream signal said the gate was needed. Without this, a partial
  // deployment (chat-handler enforcing invite + frontend rendered, but partner
  // page not yet regenerated and endpoint-status probe not yet redeployed)
  // would let the user past the gate, see the system error, and never get a
  // chance to enter a code.
  useEffect(() => {
    if (lastAuthFailureCode === "invalid_invite_code" && !authFailureLatched) {
      setAuthFailureLatched(true);
    }
  }, [lastAuthFailureCode, authFailureLatched]);

  // Set up lazy connect for both modes. Previously reachout-ai connected on
  // mount (which created an empty session in DDB for every student who
  // landed on the page but never typed). Now the widget renders a static
  // welcome prompt and only creates the session on the first user message,
  // mirroring the classic-chat pattern.
  useEffect(() => {
    if (isDistrictDisabled) return;
    if (isBotChallengeBlocking) return;
    if (inviteGateEnabled && !invite.unlocked) return;
    const m = mode as TransportMode;
    if (m === "reachout-ai") {
      setMode("reachout-ai");
      addLocalMessage({
        id: "welcome",
        role: "assistant",
        content:
          "Hey! I'm NoMi. I'm trained to listen and help. Let's talk. What can I call you?",
        timestamp: new Date().toISOString(),
      });
    } else {
      setMode("classic-chat");
      addLocalMessage({
        id: "welcome",
        role: "system",
        content:
          "Welcome! Send a message and you\u2019ll be connected with a counselor.",
        timestamp: new Date().toISOString(),
      });
    }
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    setMode,
    addLocalMessage,
    disconnect,
    isDistrictDisabled,
    isBotChallengeBlocking,
    inviteGateEnabled,
    invite.unlocked,
  ]);

  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === "auto_send" && detail.text) {
        sendMessageRef.current(detail.text);
      } else if (detail?.type === "send_message" && detail.text) {
        setInput(detail.text);
      }
    };
    window.addEventListener("interactive-action", handler);
    return () => window.removeEventListener("interactive-action", handler);
  }, []);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    const isLazyReady =
      (activeMode === "reachout-ai" || activeMode === "classic-chat") &&
      status === "disconnected";
    if (status !== "connected" && !isLazyReady) return;
    sendMessage(text);
    setInput("");
    sendMessageSent();
  }, [input, status, activeMode, sendMessage, sendMessageSent]);

  const handleBack = useCallback(() => {
    disconnect();
    sendSessionEnded("user-closed");
    navigate({ to: "/" });
  }, [disconnect, navigate, sendSessionEnded]);

  const handleFilesAdded = useCallback(
    (files: File[]) => {
      for (const file of files) {
        sendAttachment(file);
      }
    },
    [sendAttachment],
  );

  const isReachoutAI = activeMode === "reachout-ai";
  const isClassicChat = activeMode === "classic-chat";
  const isConnecting = status === "connecting";
  const isConnected = status === "connected";
  const isEnded = status === "ended";
  const isLazyReady =
    (isReachoutAI || isClassicChat) && status === "disconnected";
  const isWithCounselor =
    handoverStatus === "initiated" || handoverStatus === "connected";
  const isHandoverConnected = isReachoutAI && handoverStatus === "connected";
  const canSend = isConnected || isLazyReady;
  const canAttach = isClassicChat && isConnected;

  const isWaitingForResponse =
    isReachoutAI &&
    !isWithCounselor &&
    messages.length > 0 &&
    messages[messages.length - 1]?.role === "user" &&
    !streamingContent &&
    !isRemoteTyping;

  const isCounselorActive =
    isHandoverConnected || (isClassicChat && isConnected);
  const headerTitle = isCounselorActive
    ? "Counselor"
    : isReachoutAI
      ? "Nomi"
      : "Counselor";
  const headerAvatar = isCounselorActive
    ? "/counselor.png"
    : isReachoutAI
      ? "/nomi.png"
      : "/counselor.png";
  const headerFallback = isCounselorActive ? "C" : isReachoutAI ? "N" : "C";

  const headerStatus = (() => {
    if (isConnecting)
      return isReachoutAI ? "Connecting..." : "Connecting to counselor...";
    if (handoverStatus === "initiated") return "Connecting to a counselor...";
    if (isRemoteTyping)
      return `${remoteTypingName ?? headerTitle} is typing...`;
    if (isConnected) return "Online";
    if (isEnded) return "Chat ended";
    if (status === "reconnecting") return "Reconnecting...";
    if (isLazyReady) return "Ready";
    if (status === "disconnected") return "Disconnected";
    return "";
  })();

  if (isDistrictDisabled) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="max-w-sm">
          <h1 className="font-semibold text-lg mb-2">
            Chat is temporarily unavailable
          </h1>
          <p className="text-sm text-muted-foreground">
            Please try again later.
          </p>
        </div>
      </div>
    );
  }

  if (botChallenge.state === "pending") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="max-w-sm">
          <h1 className="font-semibold text-lg mb-3">Quick check</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Please complete this challenge to start chatting.
          </p>
          <div ref={botChallenge.container} className="flex justify-center" />
        </div>
      </div>
    );
  }

  if (botChallenge.state === "failed") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="max-w-sm">
          <h1 className="font-semibold text-lg mb-2">
            Couldn&apos;t start chat
          </h1>
          <p className="text-sm text-muted-foreground mb-4">
            {botChallenge.reason}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-sm text-primary underline underline-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (inviteGateEnabled && !invite.unlocked) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6">
        <InviteGate
          isVerifying={invite.isVerifying}
          error={invite.error}
          onSubmit={invite.submit}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <header className="flex items-center gap-3 border-b px-4 py-3 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="shrink-0"
          aria-label="Back to chat selection"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Avatar className="h-9 w-9 border">
          <AvatarImage src={headerAvatar} alt={headerTitle} />
          <AvatarFallback>{headerFallback}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm">{headerTitle}</p>
          <p className="text-xs text-muted-foreground">{headerStatus}</p>
        </div>
      </header>

      <ChatContainerRoot className="flex-1 min-h-0">
        <ChatContainerContent className="gap-3 px-4 py-4">
          {isConnecting && isClassicChat && (
            <div className="flex items-center justify-center py-12">
              <Loader
                variant="text-shimmer"
                text="Connecting to counselor..."
              />
            </div>
          )}

          {error && (
            <div className="mx-auto max-w-md rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center text-sm text-destructive">
              {error}
            </div>
          )}

          {messages
            .filter((msg) => !msg.systemEvent)
            .map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                isReachoutAI={isReachoutAI}
                onDownloadAttachment={getAttachmentUrl}
              />
            ))}

          {isRemoteTyping && !streamingContent && (
            <Message>
              <Loader variant="typing" size="sm" />
            </Message>
          )}

          {isWaitingForResponse && !isRemoteTyping && (
            <Message>
              <Loader variant="typing" size="sm" />
            </Message>
          )}

          {isEnded && (
            <div className="text-center text-sm text-muted-foreground py-4">
              This chat has ended.{" "}
              <button
                type="button"
                onClick={handleBack}
                className="text-primary underline underline-offset-2 hover:no-underline"
              >
                Start a new conversation
              </button>
            </div>
          )}
        </ChatContainerContent>

        <ScrollToBottomButton />
      </ChatContainerRoot>

      {isReachoutAI && canSwitchToCounselor && !isEnded && !isWithCounselor && (
        <div className="shrink-0 px-4 pt-2">
          <button
            type="button"
            onClick={requestHumanAssistance}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5",
              "text-sm font-medium text-primary",
              "bg-primary/10",
              "transition-all duration-150",
              "hover:bg-primary/20 hover:shadow-sm",
              "active:bg-primary/25 active:scale-[0.98]",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
            )}
          >
            <Phone className="size-4" />
            Talk to a Counselor
          </button>
        </div>
      )}

      <div className="shrink-0 px-4 pb-4 pt-2">
        <FileUpload
          onFilesAdded={handleFilesAdded}
          accept={CONNECT_ACCEPT}
          disabled={!canAttach}
        >
          <PromptInput
            value={input}
            onValueChange={setInput}
            onSubmit={handleSend}
            isLoading={isConnecting}
            disabled={!canSend || isEnded}
          >
            <PromptInputTextarea
              placeholder={
                isEnded
                  ? "Chat ended"
                  : canSend
                    ? `Message ${headerTitle}...`
                    : "Connecting..."
              }
              onKeyDown={() => {
                if (isConnected && isClassicChat) {
                  sendTyping();
                }
              }}
            />
            <PromptInputActions className="justify-end gap-1">
              {canAttach && (
                <FileUploadTrigger
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  aria-label="Attach file"
                >
                  <Paperclip className="h-4 w-4" />
                </FileUploadTrigger>
              )}
              <PromptInputAction tooltip="Send message">
                <Button
                  variant="default"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  disabled={!input.trim() || !canSend || isEnded}
                  onClick={handleSend}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </PromptInputAction>
            </PromptInputActions>
          </PromptInput>
        </FileUpload>
      </div>
    </div>
  );
}

function MessageBubble({
  msg,
  isReachoutAI,
  onDownloadAttachment,
}: {
  msg: ChatMessage;
  isReachoutAI: boolean;
  onDownloadAttachment: (attachmentId: string) => Promise<string | null>;
}) {
  const isUser = msg.role === "user";

  return (
    <Message className={isUser ? "justify-end" : ""}>
      <div className="flex flex-col gap-1.5 max-w-[85%]">
        {msg.contentType === "interactive" && msg.interactiveSpec ? (
          <div className="rounded-lg p-2 text-foreground bg-secondary border border-border/50 wrap-break-word whitespace-normal">
            <InteractiveMessage spec={msg.interactiveSpec} />
          </div>
        ) : (
          msg.content && (
            <MessageContent
              markdown={!isUser && isReachoutAI}
              className={isUser ? "bg-primary text-primary-foreground" : ""}
            >
              {isUser ? displayUserContent(msg.content) : msg.content}
            </MessageContent>
          )
        )}

        {msg.attachments?.map((att) => (
          <AttachmentCard
            key={att.id}
            attachment={att}
            onDownload={onDownloadAttachment}
          />
        ))}
      </div>
    </Message>
  );
}

function ScrollToBottomButton() {
  const { isAtBottom } = useStickToBottomContext();

  if (isAtBottom) return null;

  return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
      <ScrollButton />
    </div>
  );
}
